import * as React from 'react';
import classnames from 'classnames';
import { Check, Eye } from 'lucide-react';
import {
  OCCUPIED_DURATIONS_SECONDS,
  OccupiedDurationSeconds,
  PRESENCE_COLORS,
  PresenceState,
  PresenceStatus,
} from '@ixicare/resources';
import { t } from '~i18n';
import { useOutsideClick } from '~hooks/useOutsideClick';
import menu from '~styles/dropdownMenu.module.css';
import StatusBadge, { toDate } from './StatusBadge';
import styles from './DutyControl.module.css';

export interface DutyGroupChoice {
  id: string;
  label: string;
}

/** Everything about the shift. Present for anyone who has a duty axis at all —
 *  omitted for profiles that only have presence, who get a plain status pill. */
export interface DutyModeProps {
  isOnDuty: boolean;
  /** Only observers may leave duty; an HCP always belongs to a group. */
  canStopDuty: boolean;
  /** Group is only switchable while you're actually handling this site. */
  canChangeGroup: boolean;
  /** Whether this user can reach more than one site (`can_view_site`). Drives
   *  how much the pill has to explain: without it there's no other site to be
   *  handling, so the duty framing is noise. */
  isMultiSite: boolean;
  /** The pill's main line — already carries the site name when it differs.
   *  Only rendered for multi-site users. */
  label: string;
  /** Group being handled, shown alongside availability on the single-site
   *  pill where there's no second line to put it on. */
  groupName?: string;
  /** Site you would go on duty at: always the one being viewed. */
  targetSiteName?: string;
  /** Site you *are* on duty at, for the open-count tooltip. */
  dutySiteName?: string;
  groupOptions: DutyGroupChoice[];
  /** The viewed site has no groups to join, so activating here takes none.
   *  Distinct from an empty `groupOptions`, which also covers "still loading"
   *  and "the request failed" — activating in those states does nothing. */
  isUnsegmentedSite: boolean;
  selectedGroupId: string | null;
  openCount: number;
  onOpenCountClick?: () => void;
  onPickGroup: (groupId: string) => void;
  /** null on an unsegmented site, where there is no group to join. */
  onStartDuty: (groupId: string | null) => void;
  onStopDuty: () => void;
}

export interface DutyControlProps {
  currentStatus: PresenceStatus | null;
  /** The exact rung the user last picked in-session, if any. Source of truth
   *  for the active highlight — backend only returns `occupiedUntil`, which
   *  shifts as time passes. */
  selectedDuration?: OccupiedDurationSeconds | null;
  onSelectOccupied: (durationSeconds: OccupiedDurationSeconds) => void;
  onSelectUnavailable: () => void;
  onClear: () => void;
  duty?: DutyModeProps;
  /** Show the current state but refuse every write — used while impersonating,
   *  where a click would change the real user's presence or alarm routing. */
  readOnly?: boolean;
  className?: string;
}

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 60 * 60;

function durationLabel(seconds: OccupiedDurationSeconds): string {
  if (seconds < SECONDS_PER_HOUR) {
    return t('presence.picker.busyMinutes', { count: Math.round(seconds / SECONDS_PER_MINUTE) });
  }
  const hours = Math.round(seconds / SECONDS_PER_HOUR);
  return hours === 1
    ? t('presence.picker.busyHour', { count: hours })
    : t('presence.picker.busyHours', { count: hours });
}

const STATE_LABEL_KEYS: Record<PresenceState, string> = {
  [PresenceState.Online]: 'presence.state.online',
  [PresenceState.Occupied]: 'presence.state.occupied',
  [PresenceState.Unavailable]: 'presence.state.unavailable',
  [PresenceState.Offline]: 'presence.state.offline',
};

const DutyControl: React.FC<DutyControlProps> = ({
  currentStatus,
  selectedDuration = null,
  onSelectOccupied,
  onSelectUnavailable,
  onClear,
  duty,
  readOnly = false,
  className,
}) => {
  const [open, setOpen] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);

  const close = React.useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useOutsideClick(wrapperRef, () => setOpen(false), open);

  const state = currentStatus?.state ?? PresenceState.Offline;
  const isOnlineSelected = state === PresenceState.Online || state === PresenceState.Offline;
  const isOccupied = state === PresenceState.Occupied;
  const isUnavailableSelected = state === PresenceState.Unavailable;
  const isOffDuty = !!duty && !duty.isOnDuty;

  // Stable primitive for memo deps — `toDate(...)` returns a fresh Date each
  // render and would defeat caching.
  const occupiedUntilMs = toDate(currentStatus?.occupiedUntil ?? null)?.getTime() ?? null;

  // Which Occupied rung to highlight. Prefer the in-session pick; otherwise
  // (page reload, cross-device sync) snap to the rung closest to the remaining
  // time so the active rung is always identifiable.
  const activeRungSeconds: OccupiedDurationSeconds | null = React.useMemo(() => {
    if (!isOccupied) return null;
    if (selectedDuration != null) return selectedDuration;
    if (occupiedUntilMs == null) return null;
    const remainingSec = Math.max(0, Math.round((occupiedUntilMs - Date.now()) / 1000));
    let best: OccupiedDurationSeconds = OCCUPIED_DURATIONS_SECONDS[0];
    let bestDiff = Math.abs(best - remainingSec);
    for (const rung of OCCUPIED_DURATIONS_SECONDS) {
      const diff = Math.abs(rung - remainingSec);
      if (diff < bestDiff) {
        best = rung;
        bestDiff = diff;
      }
    }
    return best;
  }, [isOccupied, selectedDuration, occupiedUntilMs]);

  const handleMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('button[data-menu-item]') ?? []);
    if (items.length === 0) return;
    const idx = items.indexOf(document.activeElement as HTMLButtonElement);
    const next =
      e.key === 'ArrowDown' ? items[(idx + 1) % items.length] : items[(idx - 1 + items.length) % items.length];
    next?.focus();
  };

  React.useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLButtonElement>('button[data-menu-item]')?.focus();
  }, [open]);

  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' && !open) {
      e.preventDefault();
      setOpen(true);
    }
  };

  // The duty framing only earns its space when there's a choice behind it. A
  // single-site HCP has one site and always handles its alarms, so "handling
  // alarms at X" would state the only thing that could be true.
  const showContext = !!duty && duty.isMultiSite;
  // Two lines need something to put on the bottom one. Multi-site always has it
  // (site and/or group); single-site only once a group is known.
  const stacked = showContext || !!duty?.groupName;
  // Offline stays neutral rather than green: the presence payload hasn't landed
  // yet, so claiming an on-duty colour would be getting ahead of the facts.
  const pillTone = isUnavailableSelected
    ? styles.PillUnavailable
    : isOccupied
      ? styles.PillOccupied
      : duty?.isOnDuty && state !== PresenceState.Offline
        ? styles.PillOnline
        : styles.PillNeutral;

  // Everything marking "this is the one you're on" wears the current state's
  // colour, so the menu never claims you're Online while you're Occupied.
  const selectedColor = PRESENCE_COLORS[state];

  // Going view-only doesn't clear your presence, so starting again resumes
  // whatever you were — the preview has to promise that, not Online. Offline is
  // the exception: it means presence hasn't arrived yet, not a state you chose.
  const resumeState = state === PresenceState.Offline ? PresenceState.Online : state;
  const resumeColor = PRESENCE_COLORS[resumeState];
  const resumeLabel = t(STATE_LABEL_KEYS[resumeState]);
  const selectedTint = isUnavailableSelected
    ? menu.MenuItemSelectedUnavailable
    : isOccupied
      ? menu.MenuItemSelectedOccupied
      : undefined;
  const selectedClasses = (isSelected: boolean) =>
    classnames(menu.MenuItem, isSelected && menu.MenuItemSelected, isSelected && selectedTint);

  const groupSectionLabel = duty?.isMultiSite ? t('alertMode.handlingAlarmsFor') : t('alertMode.group');

  /** Group rows in the on-duty menu, where one of them *is* your current group,
   *  so its dot is filled and checked. The off-duty list differs: nothing is
   *  selected yet, so its dots are empty rings that fill green on hover. */
  const renderGroupRow = (option: DutyGroupChoice, isSelected: boolean, onPick: () => void) => (
    <button
      key={option.id}
      type="button"
      data-menu-item
      role="menuitemradio"
      aria-checked={isSelected}
      className={selectedClasses(isSelected)}
      onClick={onPick}
    >
      <span
        className={classnames(menu.ItemDot, !isSelected && menu.ItemDotMuted)}
        style={isSelected ? { background: selectedColor } : undefined}
      />
      <span>{option.label}</span>
      {isSelected && (
        <span className={menu.Check} style={{ color: selectedColor }} aria-hidden="true">
          <Check size={14} />
        </span>
      )}
    </button>
  );

  const availabilityRows = (
    <>
      <button
        type="button"
        data-menu-item
        role="menuitemradio"
        aria-checked={isOnlineSelected}
        className={selectedClasses(isOnlineSelected)}
        onClick={() => {
          close();
          onClear();
        }}
      >
        <span className={menu.ItemDot} style={{ background: PRESENCE_COLORS[PresenceState.Online] }} />
        <span>{t('presence.state.online')}</span>
      </button>
      {OCCUPIED_DURATIONS_SECONDS.map((seconds) => {
        const isSelected = activeRungSeconds === seconds;
        return (
          <button
            key={seconds}
            type="button"
            data-menu-item
            role="menuitemradio"
            aria-checked={isSelected}
            className={selectedClasses(isSelected)}
            onClick={() => {
              close();
              onSelectOccupied(seconds);
            }}
          >
            <span className={menu.ItemDot} style={{ background: PRESENCE_COLORS[PresenceState.Occupied] }} />
            <span>{durationLabel(seconds)}</span>
          </button>
        );
      })}
      <button
        type="button"
        data-menu-item
        role="menuitemradio"
        aria-checked={isUnavailableSelected}
        className={selectedClasses(isUnavailableSelected)}
        onClick={() => {
          close();
          onSelectUnavailable();
        }}
      >
        <span className={menu.ItemDot} style={{ background: PRESENCE_COLORS[PresenceState.Unavailable] }} />
        <span>{t('presence.state.unavailable')}</span>
      </button>
    </>
  );

  return (
    <div className={classnames(styles.Wrapper, className)} ref={wrapperRef}>
      {isOffDuty ? (
        <button
          ref={triggerRef}
          type="button"
          className={styles.StartButton}
          disabled={readOnly}
          title={readOnly ? t('alertMode.impersonationReadOnly') : undefined}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          onKeyDown={handleTriggerKeyDown}
        >
          <span className={styles.StartDot} aria-hidden="true" />
          <span>{t('alertMode.startHandling')}</span>
          <span className={styles.StartCaret} aria-hidden="true">
            ▾
          </span>
        </button>
      ) : (
        <div className={classnames(styles.Pill, pillTone)}>
          <button
            ref={triggerRef}
            type="button"
            className={styles.Trigger}
            disabled={readOnly}
            title={readOnly ? t('alertMode.impersonationReadOnly') : undefined}
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            onKeyDown={handleTriggerKeyDown}
          >
            {stacked ? (
              <>
                <span className={styles.Dot} style={{ background: PRESENCE_COLORS[state] }} aria-hidden="true" />
                <span className={styles.Labels}>
                  {/* Always the real StatusBadge, so the Occupied countdown
                      ticks here as it does everywhere else. Multi-site prefixes
                      the duty framing; single-site has nothing to qualify. */}
                  <span className={styles.Eyebrow}>
                    {showContext && `${t('alertMode.handlingAlarms')} · `}
                    <StatusBadge status={currentStatus} hideDot className={styles.EyebrowBadge} />
                  </span>
                  <span className={styles.Value}>{showContext ? duty!.label : duty!.groupName}</span>
                </span>
              </>
            ) : (
              /* No group to name, so there's no second line to justify — the
                 pill is just availability. */
              <StatusBadge status={currentStatus} />
            )}
            {!readOnly && (
              <span className={styles.Caret} aria-hidden="true">
                ▾
              </span>
            )}
          </button>

          {/* Sibling of the trigger, inside the pill: it looks like one control
              but a button nested in a button is invalid HTML. */}
          {!!duty?.openCount && duty.onOpenCountClick && (
            <button
              type="button"
              className={styles.Count}
              onClick={duty.onOpenCountClick}
              title={t('alertMode.openAlertsAt', { count: duty.openCount, site: duty.dutySiteName ?? '' })}
            >
              <span className={styles.CountDot} aria-hidden="true" />
              {t('alertMode.openShort', { count: duty.openCount })}
            </button>
          )}
        </div>
      )}

      {open && (
        <div ref={menuRef} className={classnames(menu.Menu, menu.MenuRight)} role="menu" onKeyDown={handleMenuKeyDown}>
          {isOffDuty ? (
            <>
              {/* Always the site you're viewing — there is no site picker here,
                  because going on duty somewhere you can't see makes no sense.
                  The header states what picking a row does, so the rows are the
                  action: one click, no confirm step to repeat it. */}
              <div className={menu.MenuHeader}>
                {t('alertMode.handleAlarmsAt', { site: duty!.targetSiteName ?? '' })}
              </div>
              {duty!.groupOptions.length === 0 && !duty!.isUnsegmentedSite ? (
                /* Groups haven't arrived (or the request failed). Offering the
                   unsegmented row here would look identical but do nothing —
                   activation without a group is a no-op on a segmented site. */
                <span className={classnames(menu.MenuItem, styles.MenuItemInert)}>{t('global.loading')}</span>
              ) : duty!.groupOptions.length > 0 ? (
                duty!.groupOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    data-menu-item
                    role="menuitem"
                    className={menu.MenuItem}
                    onClick={() => {
                      close();
                      duty!.onStartDuty(option.id);
                    }}
                  >
                    <span
                      className={classnames(menu.ItemDot, menu.ItemDotPreview)}
                      style={{ '--preview-color': resumeColor } as React.CSSProperties}
                      aria-hidden="true"
                    />
                    <span>{option.label}</span>
                  </button>
                ))
              ) : (
                /* Unsegmented site: no group to join, so the one row is the
                   action itself. */
                <button
                  type="button"
                  data-menu-item
                  role="menuitem"
                  className={menu.MenuItem}
                  onClick={() => {
                    close();
                    duty!.onStartDuty(null);
                  }}
                >
                  <span className={classnames(menu.ItemDot, menu.ItemDotPreview)} aria-hidden="true" />
                  <span>{resumeLabel}</span>
                </button>
              )}
            </>
          ) : (
            <>
              {/* role="group" per section: the rows are two independent
                  single-choice sets, and without the grouping a screen reader
                  reads eleven radios in one flat list. The visible label is
                  aria-hidden because the group already carries it. */}
              {duty?.canChangeGroup && duty.groupOptions.length > 0 && (
                <>
                  <div role="group" aria-label={groupSectionLabel}>
                    {/* Same simplification as the pill: with one site there's no
                        "handling" to qualify, it's just which group. */}
                    <div className={menu.SectionLabel} aria-hidden="true">
                      {groupSectionLabel}
                    </div>
                    {duty.groupOptions.map((option) =>
                      renderGroupRow(option, duty.selectedGroupId === option.id, () => {
                        close();
                        duty.onPickGroup(option.id);
                      })
                    )}
                  </div>
                  <div className={menu.Divider} role="separator" />
                </>
              )}
              <div role="group" aria-label={t('profiles.table.status')}>
                {/* The same word the profiles table and the group aggregate use
                    for presence — one concept, one label. */}
                {duty && (
                  <div className={menu.SectionLabel} aria-hidden="true">
                    {t('profiles.table.status')}
                  </div>
                )}
                {availabilityRows}
              </div>
              {/* Separated from Unavailable on purpose: that's a temporary
                  personal mute while you stay on the roster, this leaves it
                  altogether. */}
              {duty?.canStopDuty && (
                <>
                  <div className={menu.Divider} role="separator" />
                  <button
                    type="button"
                    data-menu-item
                    role="menuitem"
                    className={menu.MenuItem}
                    onClick={() => {
                      close();
                      duty.onStopDuty();
                    }}
                  >
                    <Eye size={14} aria-hidden="true" />
                    <span>{t('alertMode.stopViewOnly')}</span>
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DutyControl;
